import { useNavigate } from "react-router-dom";

import { reporterGenreOfChannel } from "@/entities/scene-card/model/presets";
import { REPORTER_DEST_LABEL } from "@/entities/work/model/deptTitles";
import { Card, CardContent } from "@/shared/ui/card";

import type { GeoPlanArticle, GeoReport } from "../../../api/geoService";
import { rankWithBrand } from "../../../lib/competitorRank";
import { BrandPortrait, NegativeExposure } from "../report/InsightCards";
import { StrategyTopics } from "../report/StrategyTopics";
import { SecNo } from "./shared";
import { platformLabel } from "../../../lib/platformLabel";
import { formatScoreDeltaAbs, roundDelta } from "../../../lib/scoreDelta";
import { stripScoreVersionCodes } from "../../../lib/scoreVersionLabel";

// 周条头配色(2026-07-29:内容计划从「6 月 × 5 篇 = 30」改为「4 周 × 4 篇 = 16」,
// 原「三阶段 × 两个月」的分列口径作废 —— 现在一周一列,主题直接取后端的 theme,
// 不再在前端写死月份区间与阶段名,免得跟后端主题轴各说各的)。
const WEEK_HEAD_CLS = [
  "from-indigo-500 to-violet-500",
  "from-cyan-600 to-sky-500",
  "from-emerald-600 to-teal-500",
  "from-amber-500 to-rose-500",
];

/**
 * 本品牌在正面榜里算不算「未被提及」—— 与 Hero 竞品位次卡同口径:有 ai 同池位次
 * (`search_overview.brand_rank`)就看 position==null,没有就看计数为 0。
 */
function isOwnUnmentioned(report: GeoReport, count: number): boolean {
  const br = report.search_overview?.brand_rank;
  if (br) return br.position == null;
  return count === 0;
}

/**
 * 02 竞品可见度:正负双榜 + 红框问题 + 认知缺口(旧卡内容收编)。
 * 0907 重排:正面排行条 highlight 本品牌(见 `rankWithBrand`)——算不出本品牌位置时
 * (探针/竞品排名任一缺席)老实退回纯竞品榜,不编数字。
 */
export function CompetitorDual({ report }: { report: GeoReport }) {
  const ranking = report.competitor_ranking ?? [];
  // 负面榜:疑虑面负向探针里出现的竞品计数(实测面;推演无竞品抽取)
  const negCounts = new Map<string, number>();
  for (const p of report.probes ?? []) {
    if (p.stage === "doubt" && p.sentiment === "negative") {
      for (const c of p.competitors ?? []) negCounts.set(c, (negCounts.get(c) ?? 0) + 1);
    }
  }
  const negRows = [...negCounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
  const maxNeg = Math.max(negRows[0]?.[1] ?? 1, 1);
  const lost = (report.recommendations?.topics ?? [])
    .map((t) => t.target_question)
    .filter(Boolean)
    .slice(0, 3);

  // 正面榜:能算出本品牌位置就用合并榜(前 6 + 本品牌若掉出前 6 则补一行置底);
  // 算不出就是原始纯竞品榜——两种情况下都补一列真实排名号(mockup 排行条的编号)。
  const combined = rankWithBrand(report);
  const posRows = combined
    ? (() => {
        const top = combined.slice(0, 6).map((r, i) => ({ ...r, rank: i + 1 }));
        const ownIdx = combined.findIndex((r) => r.own);
        return ownIdx < 6 ? top : [...top, { ...combined[ownIdx], rank: ownIdx + 1 }];
      })()
    : ranking.map(([name, count], i) => ({ name, count, own: false, rank: i + 1 })).slice(0, 6);
  const maxPos = Math.max(posRows[0]?.count ?? 1, 1);

  return (
    <section id="geo-sec-comp" className="space-y-2">
      <SecNo no="02" title="竞品可见度" desc="正面/负面双榜 · 竞品提及不计入本品牌得分" />
      <Card>
        <CardContent className="grid gap-4 p-6 md:grid-cols-2">
          <div>
            <h6 className="mb-2 text-xs font-bold">
              正面可见度排行 <span className="text-emerald-600">Positive</span>
            </h6>
            {posRows.length === 0 && (
              <div className="text-xs text-muted-foreground">未发现竞品</div>
            )}
            {posRows.map((r) => {
              // 1006:本品牌 0 提及时不给名次(此前补行写「101」= 竞品数 + 1),与 Hero 位次卡同口径「未被提及」
              const unmentioned = r.own && isOwnUnmentioned(report, r.count);
              return (
                <div
                  key={r.name}
                  data-testid={r.own ? "geo-comp-own-row" : undefined}
                  className={`mb-1.5 grid items-center gap-2 rounded-md text-2xs ${
                    r.own
                      ? "grid-cols-[20px_minmax(92px,auto)_1fr_52px] border border-primary/30 bg-primary/7 px-1.5 py-1"
                      : "grid-cols-[20px_92px_1fr_52px]"
                  }`}
                >
                  <span
                    data-testid={r.own ? "geo-comp-own-rank" : undefined}
                    className={`text-center font-mono ${r.own ? "font-bold text-primary" : "text-muted-foreground"}`}
                  >
                    {unmentioned ? "—" : r.rank}
                  </span>
                  <span className="flex min-w-0 items-center gap-1">
                    {/* 1006:品牌名被截成「云答…」—— 本品牌行不截断(列宽随名字),竞品行悬停看全名 */}
                    <b
                      title={r.name}
                      className={r.own ? "whitespace-nowrap text-primary" : "truncate"}
                    >
                      {r.name}
                    </b>
                    {r.own && (
                      <span className="shrink-0 rounded-full bg-primary px-1.5 py-px text-[9.5px] font-bold text-primary-foreground">
                        本品牌
                      </span>
                    )}
                  </span>
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <i
                      className={`block h-full ${r.own ? "bg-primary" : "bg-emerald-500/75"}`}
                      style={{ width: `${(r.count / maxPos) * 100}%` }}
                    />
                  </div>
                  <span
                    data-testid={r.own ? "geo-comp-own-count" : undefined}
                    className={`text-right font-mono text-2xs ${r.own ? "font-bold text-primary" : "text-muted-foreground"}`}
                  >
                    {unmentioned ? "未被提及" : `${r.count} 次`}
                  </span>
                </div>
              );
            })}
          </div>
          <div>
            <h6 className="mb-2 text-xs font-bold text-rose-500">负面可见度排行 Negative</h6>
            {negRows.length === 0 && (
              <div className="text-xs text-muted-foreground">疑虑面未发现竞品负面点名</div>
            )}
            {negRows.map(([name, count]) => (
              <div
                key={name}
                className="mb-1.5 grid grid-cols-[88px_1fr_44px] items-center gap-2 text-2xs"
              >
                <b className="truncate">{name}</b>
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <i
                    className="block h-full bg-rose-500/65"
                    style={{ width: `${(count / maxNeg) * 100}%` }}
                  />
                </div>
                <span className="text-right font-mono text-2xs text-muted-foreground">
                  {count} 次
                </span>
              </div>
            ))}
          </div>
          {lost.length > 0 && (
            <p className="col-span-full text-2xs text-muted-foreground">
              红框问题(竞对被推荐而本品牌缺席):{lost.map((q) => `「${q}」`).join("")} →
              已转入内容计划选题。
            </p>
          )}
        </CardContent>
      </Card>
      <div className="grid gap-3.5 md:grid-cols-2">
        <NegativeExposure report={report} />
        <BrandPortrait report={report} />
      </div>
    </section>
  );
}

/**
 * 03 诊断建议与短板:情感分布条形 + 具名短板 + 内容策略卡收编。
 * 0907 重排:短板卡改双列卡片(圆形序号 + 诊断/建议分两行)。GeoShortboard 只有
 * {no,name,why,action} 四个字段——没有危险等级、没有预计提分,mockup 的「高危/中危」药丸
 * 与「预计 +N 分」这两处**没有实现**,不编。
 */
export function DiagnosisSec({ report }: { report: GeoReport }) {
  const platforms = report.platform_details?.platforms ?? [];
  const boards = report.shortboards ?? [];
  if (!platforms.length && !boards.length) return null;
  const totals = platforms.reduce(
    (acc, p) => {
      acc.total += p.samples;
      acc.pos += p.sentiment_dist.positive;
      acc.neu += p.sentiment_dist.neutral;
      acc.neg += p.sentiment_dist.negative;
      return acc;
    },
    { total: 0, pos: 0, neu: 0, neg: 0 }
  );
  return (
    <section id="geo-sec-diagnosis" className="space-y-2">
      <SecNo no="03" title="诊断建议与短板" desc="平台情感分布 + 具名短板" />
      <Card>
        <CardContent className="p-6">
          <div className="mb-3 text-xs text-muted-foreground">
            合计问询 <b className="tabular-nums">{totals.total}</b> · 正向{" "}
            <b className="tabular-nums text-emerald-600">{totals.pos}</b> · 中性{" "}
            <b className="tabular-nums">{totals.neu}</b> · 负向{" "}
            <b className="tabular-nums text-rose-500">{totals.neg}</b>
          </div>
          <div data-testid="geo-sentiment-dist">
            {platforms.map((p) => {
              const n = Math.max(p.samples, 1);
              return (
                <div
                  key={p.platform}
                  className="mb-1.5 grid grid-cols-[86px_1fr_86px] items-center gap-2 text-2xs"
                >
                  <span className="truncate">
                    {platformLabel(p.label)}
                    {p.provenance === "real" ? "★" : ""}
                  </span>
                  <div className="flex h-[11px] overflow-hidden rounded-md bg-muted">
                    <i
                      className="block h-full bg-emerald-500/80"
                      style={{ width: `${(p.sentiment_dist.positive / n) * 100}%` }}
                    />
                    <i
                      className="block h-full bg-muted-foreground/30"
                      style={{ width: `${(p.sentiment_dist.neutral / n) * 100}%` }}
                    />
                    <i
                      className="block h-full bg-rose-500/65"
                      style={{ width: `${(p.sentiment_dist.negative / n) * 100}%` }}
                    />
                  </div>
                  <span className="text-right font-mono text-2xs text-muted-foreground">
                    {p.sentiment_dist.positive} / {p.sentiment_dist.neutral} /{" "}
                    {p.sentiment_dist.negative}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="mb-3 mt-1 flex flex-wrap gap-3 text-2xs text-muted-foreground">
            <span>正向 / 中性提及 / 负向</span>
            <span>★ = 实测,其余推演</span>
          </div>
          <div data-testid="geo-shortboards" className="grid gap-3 sm:grid-cols-2">
            {boards.map((b) => (
              <div
                key={b.no}
                className="flex flex-col gap-2 rounded-xl border border-l-[3px] border-l-amber-500/70 bg-background px-4 py-3.5 text-2xs leading-relaxed"
              >
                <div className="flex items-center gap-2">
                  <span className="flex h-5 w-11 flex-none items-center justify-center rounded-full bg-amber-500/15 font-mono text-[10.5px] font-extrabold text-amber-600">
                    {b.no}
                  </span>
                  <b className="text-[13px]">{b.name}</b>
                </div>
                <p className="text-muted-foreground">{b.why}</p>
                <p>
                  <b className="font-bold">建议:</b>
                  <span className="text-muted-foreground">{b.action}</span>
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      {/* 旧「内容策略卡(怎么写怎么投)」收编(内容不丢) */}
      <StrategyTopics report={report} />
    </section>
  );
}

/** 10 内容投放计划:首月 4 周 × 4 篇卡墙;每篇「送新闻舱成稿」已点亮(S2-T1)。 */
export function ContentPlanStages({ report }: { report: GeoReport }) {
  const navigate = useNavigate();
  const plan = report.content_plan;
  if (!plan?.available || !plan.months?.length) return null;
  // 渠道→体裁映射走 presets.ts 单源;种子只预填素材框(可编辑),不静默直发
  const sendToReporter = (a: GeoPlanArticle) =>
    navigate("/ai-jizhe/create", {
      state: {
        scenePreset: {
          kind: "reporter",
          genre: reporterGenreOfChannel(a.channel),
          sourceLabel: `来自GEO雷达·第${a.week ?? a.month}周计划`,
          materialSeed: {
            title: a.title,
            userQuestion: a.target_question,
            channel: a.channel,
            audience: a.audience,
            keywords: [a.content_type, a.purpose].filter(Boolean),
          },
        },
      },
    });
  const weeks = plan.weeks ?? plan.months;
  const cadence =
    plan.cadence?.label ?? `${weeks.length} 周 × 每周 ${weeks[0]?.articles?.length ?? 4} 篇`;
  return (
    <section id="geo-sec-plan" className="space-y-2">
      <SecNo
        no="10"
        title="内容投放计划"
        desc={`${cadence} = ${plan.total ?? weeks.length * 4} 篇 · 首月执行排期`}
      />
      <Card>
        <CardContent className="p-6">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4" data-testid="geo-content-plan">
            {weeks.map((wk, wi) => {
              return (
                <div key={wk.week ?? wk.month}>
                  <div
                    className={`mb-2 flex flex-col gap-0.5 rounded-lg bg-gradient-to-r px-3 py-2 text-xs font-extrabold text-white ${
                      WEEK_HEAD_CLS[wi % WEEK_HEAD_CLS.length]
                    }`}
                  >
                    第 {wk.week ?? wk.month} 周
                    <span className="text-2xs font-normal opacity-85">{wk.theme}</span>
                  </div>
                  {wk.articles.map((a) => (
                    <div
                      key={a.no}
                      className="mb-2 rounded-lg border bg-background px-3 py-2 text-2xs leading-normal"
                    >
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 flex-none rounded-md border border-primary/35 px-1.5 font-mono text-2xs font-extrabold text-primary">
                          {String(a.no).padStart(2, "0")}
                        </span>
                        <b className="font-bold">{a.title}</b>
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        <span className="rounded-full bg-muted px-2 py-0.5 text-2xs text-muted-foreground">
                          {a.channel}
                        </span>
                        <span className="rounded-full bg-muted px-2 py-0.5 text-2xs text-muted-foreground">
                          {a.content_type}
                        </span>
                        <span className="rounded-full bg-muted px-2 py-0.5 text-2xs text-muted-foreground">
                          {a.affects_note}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => sendToReporter(a)}
                        title="标题/问题/受众带入新闻舱创建页,关键词结构化直落关键词区"
                        className="mt-1.5 text-2xs font-bold text-primary hover:underline"
                        data-testid="geo-send-reporter"
                      >
                        {/* 「送」+ 单源目的地名(EXP-RPT-04:与追爆两处入口同名) */}送
                        {REPORTER_DEST_LABEL} →
                      </button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
          {plan.wording_note && (
            <div className="mt-2 text-2xs text-muted-foreground">{plan.wording_note}</div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

/** 11 信源投放建议:当前引用来自本轮真实采集,建议权重是策略不是承诺。 */
export function SourceInvestTable({ report }: { report: GeoReport }) {
  const inv = report.source_investment;
  if (!inv?.available || !inv.rows?.length) return null;
  const stars = (w: string) => (w === "高" ? "★★★" : w === "中" ? "★★" : "★");
  return (
    <section id="geo-sec-invest" className="space-y-2">
      <SecNo
        no="11"
        title="信源投放建议"
        desc="当前引用次数来自本轮真实采集 · 建议权重是策略不是承诺"
      />
      <Card>
        <CardContent className="overflow-x-auto p-6">
          <table className="w-full border-collapse text-xs" data-testid="geo-source-invest">
            <thead>
              <tr className="border-b text-left text-2xs text-muted-foreground">
                <th className="px-2 py-1.5 font-semibold">信源渠道</th>
                <th className="px-2 py-1.5 font-semibold">当前引用</th>
                <th className="px-2 py-1.5 font-semibold">建议权重</th>
                <th className="px-2 py-1.5 font-semibold">推荐原因</th>
                <th className="px-2 py-1.5 font-semibold">对应内容主题</th>
                <th className="px-2 py-1.5 font-semibold">预计影响的问题类型</th>
              </tr>
            </thead>
            <tbody>
              {inv.rows.map((r) => (
                <tr key={r.source} className="border-b last:border-b-0">
                  <td className="px-2 py-1.5 font-semibold">{r.source}</td>
                  <td className="px-2 py-1.5 font-mono tabular-nums">{r.current_citations}</td>
                  <td className="px-2 py-1.5">{stars(r.suggested_weight)}</td>
                  <td className="px-2 py-1.5">{r.reason}</td>
                  <td className="px-2 py-1.5">{r.theme}</td>
                  <td className="px-2 py-1.5">{r.affects_intents.join(" · ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {inv.wording_note && (
            <div className="mt-2 text-2xs text-muted-foreground">{inv.wording_note}</div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

/** 12 基线对比:三列式逐项 delta;跨口径只列综合分并明示。 */
export function BaselineCompareSec({ report }: { report: GeoReport }) {
  const cmp = report.baseline_compare;
  if (!cmp?.available) return null;
  return (
    <section id="geo-sec-baseline" className="space-y-2">
      <SecNo no="12" title="基线对比" desc="基线轮 → 本轮 · 基线数据只读永不回改" />
      <Card>
        <CardContent className="p-6">
          <div className="mb-2 text-xs text-muted-foreground">
            基线轮:{report.baseline_compare?.baseline_at?.slice(0, 10) ?? "—"} ·{" "}
            {cmp.baseline_grade || "—"}
            {cmp.same_caliber === false && (
              <span className="ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 text-2xs font-bold text-amber-600">
                口径不同 · 仅供参考
              </span>
            )}
          </div>
          <table className="w-full border-collapse text-xs" data-testid="geo-baseline-compare">
            <thead>
              <tr className="border-b text-left text-2xs text-muted-foreground">
                <th className="px-2 py-1.5 font-semibold">指标</th>
                <th className="px-2 py-1.5 font-semibold">基线</th>
                <th className="px-2 py-1.5 font-semibold">本轮</th>
                <th className="px-2 py-1.5 font-semibold">变化</th>
              </tr>
            </thead>
            <tbody>
              {(cmp.rows ?? []).map((r) => (
                <tr key={r.key} className="border-b last:border-b-0">
                  <td className="px-2 py-1.5 font-semibold">{r.label}</td>
                  <td className="px-2 py-1.5 font-mono tabular-nums">{r.baseline ?? "—"}</td>
                  <td className="px-2 py-1.5 font-mono font-bold tabular-nums text-primary">
                    {r.current}
                  </td>
                  <td className="px-2 py-1.5 font-mono tabular-nums">
                    {r.delta == null ? (
                      "—"
                    ) : roundDelta(r.delta) > 0 ? (
                      <span className="text-emerald-600">↑ {formatScoreDeltaAbs(r.delta)}</span>
                    ) : roundDelta(r.delta) < 0 ? (
                      <span className="text-rose-500">↓ {formatScoreDeltaAbs(r.delta)}</span>
                    ) : (
                      // 与 Hero「与基线轮持平」同口径:不写裸「0」
                      <span className="text-muted-foreground">持平</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* note 是 ai 写进报告数据的原文,可能带「(v2.2-evidence)」内部口径代号 → 渲染前剥掉 */}
          {stripScoreVersionCodes(cmp.note) && (
            <div className="mt-2 text-2xs text-muted-foreground">
              {stripScoreVersionCodes(cmp.note)}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
